import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Container, Stack, Text, Code, Button, Group } from "@mantine/core";
import { IconBrandApple, IconBrandGooglePlay, IconCopy } from "@tabler/icons-react";
import { GooglePlayStoreDownloadLink } from "../../lib/data";

/**
 * Standard invite landing page:
 *   https://nakuzisa.com/join?ref=CODE
 *
 * - Shows the referral code
 * - Tries to open the app via custom scheme
 * - Offers Play / App Store download
 */
export default function Join() {
  const [params] = useSearchParams();
  const raw = params.get("ref") || params.get("code") || "";
  const code = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

  const appDeepLink = useMemo(
    () => (code ? `virel://join?ref=${encodeURIComponent(code)}` : "virel://"),
    [code],
  );

  const tryOpenApp = () => {
    window.location.href = appDeepLink;
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // ignore
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <Container size="sm">
        <Stack align="center" gap="xl">
          <img
            src="/icon.png"
            alt="Virel Logo"
            className="w-28 h-28 md:w-36 md:h-36 rounded-3xl"
          />

          <div className="text-center space-y-2 max-w-xl">
            <h1 className="text-3xl md:text-5xl font-black text-[#10B981] tracking-tight">
              You&apos;re invited to Virel
            </h1>
            <Text size="lg" className="text-slate-600 font-medium">
              Complete opportunities and earn money. Use the invite code below
              when you create your account.
            </Text>
          </div>

          {code ? (
            <Stack align="center" gap="sm" className="w-full">
              <Text size="sm" className="text-slate-500 font-semibold uppercase tracking-wider">
                Referral code
              </Text>
              <Group gap="sm">
                <Code
                  className="text-2xl md:text-3xl font-black tracking-widest px-6 py-3"
                  color="green"
                >
                  {code}
                </Code>
                <Button
                  variant="light"
                  color="green"
                  leftSection={<IconCopy size={16} />}
                  onClick={copyCode}
                >
                  Copy
                </Button>
              </Group>
              <Button
                size="md"
                color="green"
                radius="xl"
                onClick={tryOpenApp}
                className="mt-2"
              >
                Open in Virel app
              </Button>
            </Stack>
          ) : (
            <Text className="text-slate-500">
              No invite code in this link — you can still download Virel below.
            </Text>
          )}

          <Group justify="center" gap="md" className="w-full mt-4">
            <a
              href="https://apps.apple.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <div className="flex items-center gap-4 bg-[#10B981] px-8 py-4 rounded-2xl transition-all hover:bg-[#059669] hover:scale-105">
                <IconBrandApple size={36} className="text-white" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] text-emerald-100 font-bold uppercase tracking-wider">
                    Download on the
                  </span>
                  <span className="text-xl font-bold text-white leading-none">
                    App Store
                  </span>
                </div>
              </div>
            </a>

            <a
              href={GooglePlayStoreDownloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <div className="flex items-center gap-4 bg-[#10B981] px-8 py-4 rounded-2xl transition-all hover:bg-[#059669] hover:scale-105">
                <IconBrandGooglePlay size={36} className="text-white" />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] text-emerald-100 font-bold uppercase tracking-wider">
                    Get it on
                  </span>
                  <span className="text-xl font-bold text-white leading-none">
                    Google Play
                  </span>
                </div>
              </div>
            </a>
          </Group>

          <Text size="sm" className="text-slate-400 text-center max-w-md">
            Already have the app? Tap &quot;Open in Virel app&quot;, or enter the
            code after you sign up on the Referral screen.
          </Text>
        </Stack>
      </Container>
    </div>
  );
}
